import {
  Box,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import type { BiologicalSex } from "@trainwell/features/clients";
import { useEffect, useState } from "react";
import { trainwellColor } from "src/theme/themePrimitives";
import { useButtonStore } from "../components/ButtonState";
import { FreeboardingParagraph } from "../components/FreeboardingParagraph";
import { FreeboardingTitle } from "../components/FreeboardingTitle";
import OutlinedCard from "../components/OutlinedCard";
import { TargetIcon } from "../components/TargetIcon";

interface ProfileData {
  weightSystem: "metric" | "imperial";
  age: string;
  heightInches: string;
  weight: string;
  biologicalSex: BiologicalSex | undefined;
}

interface ProfileInputProps {
  titleText: string;
  bodyText: string;
  profile: ProfileData | null;
  onProfileChange: (profile: ProfileData | null) => void;
}

const ProfileInput = ({
  titleText,
  bodyText,
  profile,
  onProfileChange,
}: ProfileInputProps) => {
  // UI state for feet/inches input
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [weightKg, setWeightKg] = useState("");

  // Local state for form validation
  const [localProfile, setLocalProfile] = useState<ProfileData>({
    weightSystem: "imperial",
    age: profile?.age || "",
    heightInches: profile?.heightInches || "",
    weight: profile?.weight || "",
    biologicalSex: profile?.biologicalSex,
  });

  const [isValid, setIsValid] = useState(false);
  const setButtonEnabled = useButtonStore((state) => state.setButtonEnabled);

  // Validation functions
  const validateAge = (age: string): boolean => {
    const ageNum = parseInt(age);
    return age !== "" && !isNaN(ageNum) && ageNum >= 13 && ageNum <= 120;
  };

  const validateHeight = (feet: string, inches: string): boolean => {
    const feetNum = parseInt(feet);
    const inchesNum = parseInt(inches);
    return (
      feet !== "" &&
      inches !== "" &&
      !isNaN(feetNum) &&
      !isNaN(inchesNum) &&
      inchesNum >= 0 &&
      inchesNum <= 11 &&
      feetNum >= 4 &&
      feetNum <= 8
    );
  };

  const validateWeight = (weight: string): boolean => {
    const weightNum = parseInt(weight);
    return (
      weight !== "" && !isNaN(weightNum) && weightNum >= 50 && weightNum <= 500
    );
  };

  // Initialize height feet/inches from total inches if available
  useEffect(() => {
    if (profile?.heightInches) {
      const totalInches = parseInt(profile.heightInches);
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      setHeightFeet(feet.toString());
      setHeightInches(inches.toString());
    }
  }, [profile?.heightInches]);

  // Update local profile when prop changes
  useEffect(() => {
    setLocalProfile({
      weightSystem: profile?.weightSystem || "imperial",
      age: profile?.age || "",
      heightInches: profile?.heightInches || "",
      weight: profile?.weight || "",
      biologicalSex: profile?.biologicalSex,
    });
  }, [profile]);

  // Handle changes to the height inputs
  const handleHeightFeetChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const feet = event.target.value;
    setHeightFeet(feet);

    // Convert feet and inches to total inches
    const totalInches =
      feet !== "" && heightInches !== ""
        ? (parseInt(feet) * 12 + parseInt(heightInches)).toString()
        : "";

    const updatedProfile = {
      ...localProfile,
      heightInches: totalInches,
    };

    setLocalProfile(updatedProfile);
    onProfileChange(updatedProfile);
  };

  const handleHeightInchesChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inches = event.target.value;
    setHeightInches(inches);

    // Convert feet and inches to total inches
    const totalInches =
      heightFeet !== "" && inches !== ""
        ? (parseInt(heightFeet) * 12 + parseInt(inches)).toString()
        : "";

    const updatedProfile = {
      ...localProfile,
      heightInches: totalInches,
    };

    setLocalProfile(updatedProfile);
    onProfileChange(updatedProfile);
  };

  const handleInputChange =
    (field: keyof ProfileData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const updatedProfile = {
        ...localProfile,
        [field]: newValue,
      };

      setLocalProfile(updatedProfile);
      onProfileChange(updatedProfile);
    };

  // Validate all fields
  useEffect(() => {
    const isAgeValid = validateAge(localProfile.age);
    const isHeightValid = validateHeight(heightFeet, heightInches);
    const isWeightValid = validateWeight(localProfile.weight);
    const hasBiologicalSex = localProfile.biologicalSex !== undefined;

    setIsValid(
      isAgeValid && isHeightValid && isWeightValid && hasBiologicalSex,
    );
  }, [localProfile, heightFeet, heightInches]);

  // Update button state based on validation
  useEffect(() => {
    setButtonEnabled(isValid);
  }, [isValid, setButtonEnabled]);

  // Handle unit system change
  const handleUnitSystemChange = (
    _: unknown,
    newSystem: "metric" | "imperial" | null,
  ) => {
    if (newSystem === null) return;

    // Convert values when switching systems
    if (newSystem === "metric" && localProfile.weightSystem === "imperial") {
      // Convert imperial to metric
      const weightKg = localProfile.weight
        ? (parseFloat(localProfile.weight) * 0.453592).toFixed(1)
        : "";
      const heightCm = localProfile.heightInches
        ? (parseFloat(localProfile.heightInches) * 2.54).toFixed(1)
        : "";
      setWeightKg(weightKg);
      setHeightCm(heightCm);
    } else if (
      newSystem === "imperial" &&
      localProfile.weightSystem === "metric"
    ) {
      // Convert metric to imperial
      const weightLbs = weightKg
        ? (parseFloat(weightKg) * 2.20462).toFixed(1)
        : "";
      const totalInches = heightCm
        ? (parseFloat(heightCm) / 2.54).toFixed(1)
        : "";
      const feet = Math.floor(parseFloat(totalInches) / 12);
      const inches = Math.round(parseFloat(totalInches) % 12);
      setHeightFeet(feet.toString());
      setHeightInches(inches.toString());

      const updatedProfile = {
        ...localProfile,
        weightSystem: newSystem,
        weight: weightLbs,
        heightInches: totalInches,
      };
      setLocalProfile(updatedProfile);
      onProfileChange(updatedProfile);
      return;
    }

    const updatedProfile = {
      ...localProfile,
      weightSystem: newSystem,
    };
    setLocalProfile(updatedProfile);
    onProfileChange(updatedProfile);
  };

  // Handle metric input changes
  const handleMetricInputChange =
    (field: "height" | "weight") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (field === "height") {
        setHeightCm(value);
        const totalInches = value ? (parseFloat(value) / 2.54).toFixed(1) : "";
        const updatedProfile = {
          ...localProfile,
          heightInches: totalInches,
        };
        setLocalProfile(updatedProfile);
        onProfileChange(updatedProfile);
      } else {
        setWeightKg(value);
        const weightLbs = value ? (parseFloat(value) * 2.20462).toFixed(1) : "";
        const updatedProfile = {
          ...localProfile,
          weight: weightLbs,
        };
        setLocalProfile(updatedProfile);
        onProfileChange(updatedProfile);
      }
    };

  return (
    <Box
      display="flex"
      maxWidth="420px"
      flexDirection="column"
      justifyContent="space-between"
      width="100%"
      height="100%"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
      >
        <FreeboardingTitle>{titleText}</FreeboardingTitle>
        <FreeboardingParagraph>{bodyText}</FreeboardingParagraph>

        <Box sx={{ width: "100%", mb: 1, mt: 2 }}>
          <ToggleButtonGroup
            value={localProfile.weightSystem}
            exclusive
            onChange={handleUnitSystemChange}
            fullWidth
          >
            <ToggleButton value="imperial" sx={{ textTransform: "none" }}>
              ft / lbs
            </ToggleButton>
            <ToggleButton value="metric" sx={{ textTransform: "none" }}>
              cm / kg
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Stack
          direction="column"
          spacing={2}
          marginTop={3}
          marginBottom={2}
          width="100%"
        >
          <TextField
            label="Age"
            type="number"
            value={localProfile.age}
            onChange={handleInputChange("age")}
            fullWidth
            variant="outlined"
          />

          {localProfile.weightSystem === "imperial" ? (
            <Box>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Feet"
                  type="number"
                  value={heightFeet}
                  onChange={handleHeightFeetChange}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Inches"
                  type="number"
                  value={heightInches}
                  onChange={handleHeightInchesChange}
                  fullWidth
                  variant="outlined"
                />
              </Stack>
            </Box>
          ) : (
            <TextField
              label="Height (cm)"
              type="number"
              value={heightCm}
              onChange={handleMetricInputChange("height")}
              fullWidth
              variant="outlined"
            />
          )}

          <TextField
            label={
              localProfile.weightSystem === "imperial"
                ? "Weight (lbs)"
                : "Weight (kg)"
            }
            type="number"
            value={
              localProfile.weightSystem === "imperial"
                ? localProfile.weight
                : weightKg
            }
            onChange={
              localProfile.weightSystem === "imperial"
                ? handleInputChange("weight")
                : handleMetricInputChange("weight")
            }
            fullWidth
            variant="outlined"
          />

          <TextField
            select
            label="Biological Sex"
            value={localProfile.biologicalSex || ""}
            onChange={handleInputChange("biologicalSex")}
            fullWidth
            variant="outlined"
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
            <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
          </TextField>
        </Stack>
      </Box>

      {isValid && (
        <Box sx={{ marginTop: 2, paddingBottom: 4 }}>
          <OutlinedCard
            text="Thanks for sharing! We'll use this to personalize your training plan."
            icon={<TargetIcon />}
            cardColor={trainwellColor.success1}
            borderColor={trainwellColor.success3}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProfileInput;
